<template>
    <b-card no-body class="p-3 mb-3">
        <!-- 헤더: 이 초안(개선 제안 target)이 어디에 반영되는지 + 현재 상태 -->
        <div class="d-flex align-items-center mb-2">
            <b-badge variant="primary" class="mr-2">{{ targetLabel }}</b-badge>
            <b-badge :variant="statusVariant" class="mr-2">{{ statusLabel }}</b-badge>
            <span class="text-muted small">
                피드백 {{ dispositionRows.length }}건 처리
            </span>
            <b-button
                v-if="canRegenerate"
                class="ml-auto"
                size="sm"
                variant="outline-primary"
                @click="regenerate"
            >
                이 초안 다시 만들기
            </b-button>
        </div>

        <!-- 식별/감사 정보 — 종결 후에도 남는다(기준 버전 · 생성 job · 저장 자산). -->
        <div v-if="metaParts.length" class="small text-muted mb-2">
            <span v-for="(m, i) in metaParts" :key="i" class="mr-2">{{ m }}</span>
        </div>

        <div v-if="generating || restoring" class="text-center text-muted py-3">
            <b-spinner small class="mr-2" />
            <template v-if="restoring">이전 생성 결과를 불러오고 있습니다.</template>
            <template v-else>
                개정본을 생성하고 있습니다<span v-if="elapsedLabel"> ({{ elapsedLabel }} 경과)</span>.
                보통 1분 내외가 걸립니다.
            </template>
        </div>

        <!-- 폴링 시간 초과 — 상태가 생성 중 그대로라 액션 바가 없다. 같은 job 의 상태만 다시 조회한다(새 생성·비용 없음). -->
        <b-alert v-if="timedOut && !busy" show variant="warning" class="mb-2">
            <div>생성이 10분 넘게 걸리고 있습니다. 서버에서는 계속 진행 중일 수 있습니다.</div>
            <b-button size="sm" variant="outline-secondary" class="mt-2" @click="recheckStatus">
                상태 다시 확인
            </b-button>
        </b-alert>

        <!-- 생성 실패 — 자동 재시도하지 않고 사람이 다시 만들거나 스킵하게 한다. -->
        <b-alert v-if="isFailed && !generating && !restoring" show variant="danger" class="mb-2">
            <strong>개정본 생성에 실패했습니다.</strong>
            <div class="small mt-1">
                [이 초안 다시 만들기] 로 재생성하거나, 이 초안이 필요 없으면 [이 초안 스킵] 하세요.
            </div>
        </b-alert>

        <!-- 근거(변경 요약 + 피드백별 처리) — READY/종결 어디서든 읽기 전용으로 항상 보여준다. -->
        <template v-if="showDetail">
            <div v-if="changeSummary" class="mb-3">
                <strong class="d-block mb-1">무엇이 달라지나요</strong>
                <div class="change-summary">{{ changeSummary }}</div>
            </div>

            <div v-if="dispositionRows.length" class="mb-3">
                <strong class="d-block mb-1">내 피드백이 어떻게 반영됐나요</strong>
                <b-table
                    :items="dispositionRows"
                    :fields="dispositionFields"
                    small
                    responsive
                    show-empty
                    empty-text="반영 내역이 없습니다."
                >
                    <template #cell(action)="row">
                        <b-badge :variant="dispositionVariant(row.item.action)">
                            {{ dispositionLabel(row.item.action) }}
                        </b-badge>
                    </template>
                    <template #cell(source)="row">
                        <div class="small">
                            <div v-if="row.item.sub_code" class="text-muted">{{ row.item.sub_code }}</div>
                            <div class="disposition-content">{{ row.item.content || '(목록에 없는 피드백)' }}</div>
                        </div>
                    </template>
                    <template #cell(note)="row">
                        <span class="small">{{ row.item.note || '-' }}</span>
                    </template>
                </b-table>
            </div>
        </template>

        <!-- 편집 + 검증 — READY 이고 방금 생성한 preview 가 있을 때만(종결 target 은 편집 불가). -->
        <template v-if="isReady && preview && !generating && !restoring">
            <b-alert :show="!!validationErrors.length" variant="danger">
                <strong>이대로 저장할 수 없습니다</strong>
                <div class="small mt-1">
                    AI 가 만든 결과의 형식에 문제가 있습니다. [이 초안 다시 만들기] 를 눌러 새로 생성해 주세요.
                    반복되면 담당자에게 알려주세요.
                </div>
                <ul class="mb-0 mt-2 small text-monospace">
                    <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
                </ul>
            </b-alert>

            <!-- 판례(precedents)는 케이스별로 나눠 보고 필요한 것만 고친다. -->
            <b-card v-if="isPrecedents" no-body class="p-2 mb-3">
                <strong class="mb-2">판례 확인 / 수정</strong>
                <PrecedentCaseEditor
                    v-model="editedContent"
                    :dispositions="dispositions"
                />
            </b-card>

            <!-- 그 외 자산(yaml 등)은 원본 편집만 접어둔다. -->
            <div v-else>
                <b-button
                    v-b-toggle="`preview-raw-${target.id}`"
                    variant="link"
                    size="sm"
                    class="p-0 text-muted"
                >
                    AI 가 만든 원본 보기 / 직접 고치기
                </b-button>
                <b-collapse :id="`preview-raw-${target.id}`" lazy>
                    <div class="small text-muted mt-2 mb-1">
                        AI 가 실제로 저장할 내용입니다. 형식이 정해져 있어 잘못 고치면 저장이 거부됩니다 —
                        형식을 아는 담당자가 아니라면 그대로 두세요.
                    </div>
                    <b-form-textarea
                        v-model="editedContent"
                        rows="18"
                        class="revised-content"
                    />
                </b-collapse>
            </div>
        </template>

        <!-- 종결된 target 은 최종 상태만 알린다(근거는 위에서 이미 읽기 전용으로 보인다). -->
        <b-alert v-if="isTerminal" show :variant="terminalVariant" class="py-2 mb-0">
            {{ terminalMessage }}
        </b-alert>

        <!-- 액션 바 — READY 에서만 저장/반영불필요, PLANNED/READY/FAILED 에서 스킵. -->
        <div v-if="showActions" class="d-flex align-items-center mt-1">
            <span v-if="saveBlockedReason" class="text-danger small mr-auto">
                {{ saveBlockedReason }}
            </span>
            <span v-else class="mr-auto" />
            <b-button
                v-if="canSkip"
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                :disabled="acting"
                title="이 초안을 반영하지 않고 건너뜁니다."
                @click="skip"
            >
                이 초안 스킵
            </b-button>
            <b-button
                v-if="canConfirmNoChange"
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                :disabled="acting"
                title="검토 결과 이 자산은 바꿀 필요가 없다고 확정합니다."
                @click="confirmNoChange"
            >
                반영 불필요
            </b-button>
            <b-button
                v-if="showSaveButtons"
                variant="outline-primary"
                class="mr-2"
                :disabled="!canSave"
                title="저장만 해두고 AI 판정에는 아직 쓰지 않습니다. 나중에 버전 이력에서 적용할 수 있습니다."
                @click="save(false)"
            >
                <b-spinner v-if="saving" small class="mr-1" />
                저장만 하기
            </b-button>
            <b-button
                v-if="showSaveButtons"
                variant="primary"
                :disabled="!canSave"
                title="저장하고 곧바로 AI 판정에 사용합니다."
                @click="save(true)"
            >
                <b-spinner v-if="saving" small class="mr-1" />
                저장하고 바로 적용
            </b-button>
        </div>
    </b-card>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {
    ACCEPTED_TARGET_TERMINALS,
    DISPOSITION_LABELS,
    DISPOSITION_VARIANTS,
    previewTargetLabel,
    targetStatusLabel,
    targetStatusVariant,
} from './prompt-labels';
import PrecedentCaseEditor from './PrecedentCaseEditor.vue';

// 폴링 주기·상한 — job 하나를 이 카드가 소유한다.
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_TICKS = 240;   // 2.5초 x 240 = 10분
const POLL_MAX_ERRORS = 5;

export default {
    name: 'ApplyPreviewDraft',
    components: {PrecedentCaseEditor},
    props: {
        // 이 초안이 담당하는 proposal target(TARGET 전체). 부모가 소유하며 여기서 변이하지 않는다.
        target: {type: Object, required: true},
        // proposal 피드백 원문 — disposition 표에 sub_code/content 를 붙이는 데만 쓴다.
        feedbackItems: {type: Array, default: () => []},
    },
    data() {
        return {
            generating: false,
            restoring: false,
            saving: false,
            acting: false,
            preview: null,
            editedContent: '',
            // 폴링 상태
            jobId: null,
            pollTimer: null,
            pollInFlight: false,
            pollTicks: 0,
            pollErrorCount: 0,
            // 폴링 상한(10분)에 걸려 멈췄는지 — [상태 다시 확인] 안내용.
            timedOut: false,
            startedAt: null,
            elapsedSec: 0,
            elapsedTimer: null,
            isTornDown: false,
            dispositionFields: [
                {key: 'source', label: '내 피드백'},
                {key: 'action', label: '반영 방식', thStyle: {width: '140px'}},
                {key: 'note', label: 'AI 설명'},
            ],
        };
    },
    mounted() {
        // 진입 시점의 target 스냅샷으로 한 번만 판단한다(생성 시작/결과 복원/대기).
        // 이후 부모의 proposal 갱신으로 target prop 이 바뀌어도 job 을 재시작하지 않는다.
        this.syncFromTarget();
    },
    beforeDestroy() {
        this.isTornDown = true;
        this.stopPolling();
        this.stopElapsed();
    },
    watch: {
        // 부모의 canonical 재수화(refresh-requested)로 이 카드의 job 이 새 것으로 바뀌었는데
        // 로컬이 아직 그 job 을 따라가지 않을 때만 재동기화한다. syncFromTarget 이 mount 에서만
        // 돌기 때문에, job ID 변화를 범위로 좁혀 감시한다(다른 카드/정상 흐름은 건드리지 않음).
        'target.current_preview_job_id': function watchJobId(newId) {
            if (!newId || newId === this.jobId) return;
            if (this.generating || this.restoring || this.saving || this.acting) return;
            this.preview = null;
            this.timedOut = false;
            this.syncFromTarget();
        },
    },
    computed: {
        assetType() {
            return this.target.asset_type;
        },
        targetLabel() {
            return previewTargetLabel(this.assetType);
        },
        statusLabel() {
            return targetStatusLabel(this.target.status);
        },
        statusVariant() {
            return targetStatusVariant(this.target.status);
        },
        isReady() {
            return this.target.status === 'READY';
        },
        isFailed() {
            return this.target.status === 'FAILED';
        },
        isTerminal() {
            return ACCEPTED_TARGET_TERMINALS.includes(this.target.status)
                || this.target.status === 'CANCELLED';
        },
        busy() {
            return this.generating || this.restoring || this.saving || this.acting;
        },
        terminalVariant() {
            const s = this.target.status;
            if (s === 'ACTIVE') return 'success';
            if (s === 'SAVED') return 'info';
            return 'secondary';
        },
        terminalMessage() {
            const s = this.target.status;
            if (s === 'ACTIVE') return `${this.targetLabel} 저장 후 활성화까지 완료했습니다.`;
            if (s === 'SAVED') return `${this.targetLabel} 을 저장했습니다(비활성). 버전 이력에서 활성화할 수 있습니다.`;
            if (s === 'NO_CHANGE') return `${this.targetLabel} 은 반영 불필요로 확정했습니다.`;
            if (s === 'SKIPPED') return `${this.targetLabel} 초안을 건너뛰었습니다.`;
            return `${this.targetLabel} 은 취소되었습니다.`;
        },
        // 기준 버전 · 생성 job · 저장 자산 — 감사/식별용, 종결 후에도 유지.
        metaParts() {
            const parts = [];
            if (this.target.base_version) parts.push(`기준 버전 v${this.target.base_version}`);
            if (this.target.current_preview_job_id) {
                parts.push(`생성 job #${this.target.current_preview_job_id}`);
            }
            if (this.target.saved_asset_id) parts.push(`저장 자산 #${this.target.saved_asset_id}`);
            return parts;
        },
        // 변경 요약 — 방금 생성한 preview 우선, 없으면 target 에 영속된 값.
        changeSummary() {
            if (this.preview && this.preview.change_summary) return this.preview.change_summary;
            return this.target.change_summary || '';
        },
        // 근거(요약/처리표)를 읽기 전용으로 보여줄지 — 생성/복원/실패 중이 아니고 표시할 내용이 있으면.
        // ⛔ FAILED 에서는 숨긴다 — target 에 남은 이전 성공 초안의 요약/dispositions 가
        //    현재 실패 결과처럼 보이는 것을 막는다(백엔드는 재생성 실패 시 옛 근거를 지우지 않는다).
        showDetail() {
            if (this.generating || this.restoring || this.isFailed) return false;
            return !!this.changeSummary || this.dispositionRows.length > 0;
        },
        // 액션 바 노출 — 종결/생성중/복원중이면 숨긴다.
        showActions() {
            if (this.generating || this.restoring || this.isTerminal) return false;
            return this.canSkip || this.canConfirmNoChange || this.showSaveButtons;
        },
        showSaveButtons() {
            return this.isReady && !!this.preview;
        },
        canRegenerate() {
            return (this.isReady || this.isFailed) && !this.busy;
        },
        canSkip() {
            return ['PLANNED', 'READY', 'FAILED'].includes(this.target.status) && !this.busy;
        },
        canConfirmNoChange() {
            return this.isReady && !this.busy;
        },
        elapsedLabel() {
            if (!this.startedAt) return '';
            const s = this.elapsedSec;
            return s >= 60 ? `${Math.floor(s / 60)}분 ${s % 60}초` : `${s}초`;
        },
        isPrecedents() {
            return this.assetType === 'precedents';
        },
        // 표시용 dispositions — 방금 생성한 결과 우선, 없으면 target 에 영속된 값.
        dispositions() {
            return (this.preview && this.preview.dispositions)
                || this.target.dispositions || [];
        },
        validationErrors() {
            if (this.preview && this.preview.validation_errors) {
                return this.preview.validation_errors;
            }
            return this.target.validation_errors || [];
        },
        contentDirty() {
            return !!this.preview && this.editedContent !== this.preview.revised_content;
        },
        canSave() {
            if (!this.showSaveButtons || this.saving || this.acting) return false;
            if (!this.editedContent.trim()) return false;
            return !this.validationErrors.length || this.contentDirty;
        },
        saveBlockedReason() {
            if (!this.showSaveButtons || this.saving) return '';
            if (!this.editedContent.trim()) return 'AI 결과가 비어 있습니다. 다시 만들어 주세요.';
            if (this.validationErrors.length && !this.contentDirty) {
                return '결과 형식에 문제가 있어 저장할 수 없습니다. [이 초안 다시 만들기] 를 눌러 주세요.';
            }
            return '';
        },
        dispositionRows() {
            const byId = {};
            this.feedbackItems.forEach((f) => {
                byId[f.id] = f;
            });
            return this.dispositions.map((d) => {
                const src = byId[d.feedback_id] || {};
                return {
                    feedback_id: d.feedback_id,
                    action: d.action,
                    target_case_id: d.target_case_id,
                    note: d.note,
                    sub_code: src.sub_code,
                    content: src.content,
                };
            });
        },
    },
    methods: {
        // target.status + current_preview_job_id 조합으로 진입 동작을 정한다(설계 §3).
        syncFromTarget() {
            if (this.preview || this.generating || this.restoring) return;
            const {status} = this.target;
            const jobId = this.target.current_preview_job_id;
            if (status === 'PLANNED') {
                if (jobId) this.enterRunning(jobId);
                else this.generate();
            } else if (status === 'GENERATING') {
                if (jobId) this.enterRunning(jobId);
                else this.generate();   // 백엔드가 같은 target 의 active job 을 reuse 한다
            } else if (status === 'READY') {
                if (jobId) this.restoreResult(jobId);
                // job 없이 READY 면 결과를 되살릴 수 없다 — 사용자가 다시 만들게 둔다.
            }
            // FAILED / 종결 상태는 자동 동작 없음.
        },
        // 버튼 전용 — LLM 비용이 들고 편집 내용이 사라지므로 확인 후 generate. 자동 진입은 generate 직접.
        async regenerate() {
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '초안 다시 만들기',
                message: `${this.targetLabel} 초안을 AI 로 다시 만듭니다(AI 비용 발생).`
                    + (this.contentDirty ? ' 직접 수정한 내용이 사라지며 되돌릴 수 없습니다.' : ''),
            });
            if (ok) this.generate();
        },
        // 새 job 접수(생성 또는 다시 만들기). payload 는 proposal_target_id 만 보낸다
        // — 백엔드가 instruction/feedback_ids 스냅샷과 다르면 409 를 던진다.
        async generate() {
            this.generating = true;
            this.preview = null;
            try {
                const {data} = await this.$axios.post(
                    '/supplement-prompt/apply-preview/jobs',
                    {proposal_target_id: this.target.id});
                if (data.proposal_target_id
                        && data.proposal_target_id !== this.target.id) {
                    this.generating = false;
                    this.$bvToast.toast('생성 대상이 어긋났습니다. 창을 다시 열어 주세요.', {
                        title: '오류', variant: 'danger',
                    });
                    return;
                }
                this.$emit('patch-target', {
                    targetId: this.target.id,
                    status: data.target_status || 'GENERATING',
                    jobId: data.job_id,
                });
                this.enterRunning(data.job_id);
            } catch (e) {
                this.generating = false;
                // 서버가 job 을 만든 뒤 실패했을 수 있어(target 은 FAILED) prop 이 어긋난다.
                // 임의 patch 대신 부모에 canonical 재수화를 요청한다.
                this.$emit('refresh-requested', {targetId: this.target.id});
                this.$bvToast.toast(getErrorMessage(e, '개정본 생성 요청에 실패했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            }
        },
        // 이미 완료된 job 의 결과만 즉시 회수해 편집 상태를 복원한다(폴링 없음).
        async restoreResult(jobId) {
            this.jobId = jobId;
            this.restoring = true;
            try {
                const {data} = await this.$axios.get(
                    `/supplement-prompt/apply-preview/jobs/${jobId}`);
                // 늦게 도착한 응답이 새 job 상태를 덮지 않도록 캡처한 jobId 와 대조한다.
                if (this.isTornDown || this.jobId !== jobId) return;
                if (data.status === 'done') {
                    this.applyResult(data.result || {});
                } else if (data.status === 'failed') {
                    this.$emit('patch-target', {
                        targetId: this.target.id,
                        status: data.target_status || 'FAILED',
                    });
                } else {
                    // 아직 안 끝났으면 폴링으로 넘어간다.
                    this.enterRunning(jobId);
                }
            } catch (e) {
                if (this.isTornDown || this.jobId !== jobId) return;
                this.$bvToast.toast(getErrorMessage(e, '개정본 결과를 불러오지 못했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                if (!this.isTornDown && this.jobId === jobId) this.restoring = false;
            }
        },
        enterRunning(jobId) {
            this.jobId = jobId;
            this.restoring = false;
            this.generating = true;
            this.timedOut = false;
            this.pollTicks = 0;
            this.pollErrorCount = 0;
            this.startElapsed();
            this.startPolling();
        },
        // 시간 초과 후 같은 job 을 다시 따라간다. startPolling 만 부르면 pollTicks 가 남아 곧바로 다시 초과된다.
        recheckStatus() {
            if (this.jobId) this.enterRunning(this.jobId);
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS);
            this.fetchStatus();
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.pollInFlight = false;
        },
        startElapsed() {
            this.stopElapsed();
            this.startedAt = Date.now();
            this.elapsedSec = 0;
            this.elapsedTimer = setInterval(() => {
                this.elapsedSec = Math.floor((Date.now() - this.startedAt) / 1000);
            }, 1000);
        },
        stopElapsed() {
            if (this.elapsedTimer) {
                clearInterval(this.elapsedTimer);
                this.elapsedTimer = null;
            }
        },
        finishPolling() {
            this.stopPolling();
            this.stopElapsed();
            this.generating = false;
        },
        applyResult(result) {
            this.preview = result;
            this.editedContent = result.revised_content || '';
        },
        async fetchStatus() {
            if (this.pollInFlight) return;
            const jobId = this.jobId;
            if (!jobId) return;

            this.pollTicks += 1;
            if (this.pollTicks > POLL_MAX_TICKS) {
                this.finishPolling();
                this.timedOut = true;
                return;
            }

            this.pollInFlight = true;
            try {
                const {data} = await this.$axios.get(
                    `/supplement-prompt/apply-preview/jobs/${jobId}`);
                if (this.isTornDown || this.jobId !== jobId) return;

                this.pollErrorCount = 0;
                if (data.status === 'done') {
                    this.finishPolling();
                    this.applyResult(data.result || {});
                    this.$emit('patch-target', {
                        targetId: this.target.id,
                        status: data.target_status || 'READY',
                    });
                } else if (data.status === 'failed') {
                    this.finishPolling();
                    this.$emit('patch-target', {
                        targetId: this.target.id,
                        status: data.target_status || 'FAILED',
                    });
                    this.$bvToast.toast(
                        data.error_message || '개정본 생성에 실패했습니다.',
                        {title: '오류', variant: 'danger'});
                }
            } catch (e) {
                if (this.isTornDown || this.jobId !== jobId) return;
                const status = e.response && e.response.status;
                this.pollErrorCount += 1;
                if (status === 404 || this.pollErrorCount >= POLL_MAX_ERRORS) {
                    this.finishPolling();
                    this.$emit('patch-target', {
                        targetId: this.target.id, status: 'FAILED',
                    });
                    this.$bvToast.toast(getErrorMessage(e, '생성 상태를 확인하지 못했습니다.'), {
                        title: '오류', variant: 'danger',
                    });
                }
            } finally {
                this.pollInFlight = false;
            }
        },
        async save(activate) {
            if (!this.canSave) return;
            if (activate) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    '저장과 동시에 활성본으로 전환합니다. 활성화 후 60초 내 판정 파이프라인에 반영됩니다. '
                    + '활성화 전 수렴 지표 탭에서 골든 게이트를 확인하는 것을 권장합니다.',
                    {title: '저장 + 활성화 확인', okTitle: '저장 + 활성화', cancelTitle: '취소'},
                );
                if (!ok) return;
            }
            this.saving = true;
            try {
                const {data} = await this.$axios.post('/supplement-prompt/assets', {
                    asset_type: this.preview.asset_type || this.assetType,
                    content: this.editedContent,
                    base_version: this.preview.base_version,
                    note: this.preview.change_summary || null,
                    activate,
                    // proposal 스냅샷은 서버가 소유하므로 target id 만 보낸다.
                    proposal_target_id: this.target.id,
                });
                if (!data.proposal || data.proposal.id !== this.target.proposal_id) {
                    this.$bvToast.toast('저장 응답에 개선 제안 정보가 없습니다. 새로고침 후 확인해 주세요.', {
                        title: '오류', variant: 'danger',
                    });
                    return;
                }
                this.$bvToast.toast(
                    activate ? '새 버전을 저장하고 활성화했습니다.' : '새 버전을 비활성 상태로 저장했습니다.',
                    {title: '완료', variant: 'success'},
                );
                this.$emit('saved', {proposal: data.proposal});
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, '버전 저장에 실패했습니다.'), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.saving = false;
            }
        },
        async skip() {
            const ok = await this.$bvModal.msgBoxConfirm(
                `${this.targetLabel} 초안을 반영하지 않고 건너뜁니다.`,
                {title: '초안 스킵', okTitle: '스킵', cancelTitle: '취소'},
            );
            if (!ok) return;
            await this.postProposalAction('skip', '초안을 건너뛰지 못했습니다.');
        },
        async confirmNoChange() {
            const ok = await this.$bvModal.msgBoxConfirm(
                `${this.targetLabel} 은 바꿀 필요가 없다고 확정합니다. 이 자산은 새 버전을 만들지 않습니다.`,
                {title: '반영 불필요 확정', okTitle: '반영 불필요', cancelTitle: '취소'},
            );
            if (!ok) return;
            await this.postProposalAction(
                'confirm-no-change', '반영 불필요로 확정하지 못했습니다.');
        },
        // skip / confirm-no-change 는 응답이 PROPOSAL_ROW 라 proposal 전체를 부모에 넘긴다.
        async postProposalAction(action, failMsg) {
            this.acting = true;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-prompt/proposals/${this.target.proposal_id}`
                    + `/targets/${this.target.id}/${action}`);
                this.$emit('proposal-updated', {proposal: data});
            } catch (e) {
                this.$bvToast.toast(getErrorMessage(e, failMsg), {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.acting = false;
            }
        },
        dispositionLabel(action) {
            return DISPOSITION_LABELS[action] || action || '-';
        },
        dispositionVariant(action) {
            return DISPOSITION_VARIANTS[action] || 'secondary';
        },
    },
};
</script>

<style scoped>
.change-summary {
    white-space: pre-wrap;
    word-break: break-word;
}

.disposition-content {
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 60px;
    overflow-y: auto;
}

.revised-content {
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 0.85rem;
}
</style>
